import type { ListParams } from '@nucleus-commerce/admin-kit/index-table';
import {
  deleteAdminV1StoresStoreIdDraftOrdersId,
  getAdminV1StoresStoreIdDraftOrders,
  getAdminV1StoresStoreIdDraftOrdersId,
  patchAdminV1StoresStoreIdDraftOrdersId,
  postAdminV1StoresStoreIdDraftOrders,
  postAdminV1StoresStoreIdDraftOrdersIdCancel,
  getAdminV1StoresStoreIdDraftOrdersIdInvoicePreview,
  getAdminV1StoresStoreIdDraftOrdersPaymentOptions,
  patchAdminV1StoresStoreIdDraftOrdersIdLinesLineId,
  postAdminV1StoresStoreIdDraftOrdersIdComplete,
  postAdminV1StoresStoreIdDraftOrdersIdDuplicate,
  postAdminV1StoresStoreIdDraftOrdersIdSendInvoice,
  type CompleteDraftOrderRequest,
  type DraftInvoicePreview,
  type DraftOrder,
  type DraftPaymentOption,
  type GetAdminV1StoresStoreIdDraftOrders200,
  type PatchAdminV1StoresStoreIdDraftOrdersIdBody,
  type PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody,
  type PostAdminV1StoresStoreIdDraftOrdersBody,
  type PostAdminV1StoresStoreIdDraftOrdersIdComplete201,
  type PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody,
} from '@nucleus-commerce/api-client/admin';

/**
 * List params as `listParams` builds them from the route's search: `q`, `sort`, the flat filter
 * params (`status=draft,invoice_sent`, `total_gte=5000`), `cursor`, `limit`.
 */
export type DraftOrderListParams = ListParams;

/** Draft orders of one store. Every response is re-priced by the backend. */
export const draftOrdersApi = {
  list: (
    storeId: string,
    params: DraftOrderListParams,
  ): Promise<GetAdminV1StoresStoreIdDraftOrders200> =>
    getAdminV1StoresStoreIdDraftOrders({ storeId }, params),
  get: (storeId: string, id: string): Promise<DraftOrder> =>
    getAdminV1StoresStoreIdDraftOrdersId({ storeId, id }),
  create: (storeId: string, body: PostAdminV1StoresStoreIdDraftOrdersBody): Promise<DraftOrder> =>
    postAdminV1StoresStoreIdDraftOrders({ storeId }, body),
  update: (
    storeId: string,
    id: string,
    body: PatchAdminV1StoresStoreIdDraftOrdersIdBody,
  ): Promise<DraftOrder> => patchAdminV1StoresStoreIdDraftOrdersId({ storeId, id }, body),
  updateLine: (
    storeId: string,
    id: string,
    lineId: string,
    body: PatchAdminV1StoresStoreIdDraftOrdersIdLinesLineIdBody,
  ): Promise<DraftOrder> =>
    patchAdminV1StoresStoreIdDraftOrdersIdLinesLineId({ storeId, id, lineId }, body),
  remove: (storeId: string, id: string): Promise<void> =>
    deleteAdminV1StoresStoreIdDraftOrdersId({ storeId, id }),
  duplicate: (storeId: string, id: string): Promise<DraftOrder> =>
    postAdminV1StoresStoreIdDraftOrdersIdDuplicate({ storeId, id }),
  /** The quote email as the worker would send it now; `message` previews the personal line. */
  invoicePreview: (storeId: string, id: string, message: string): Promise<DraftInvoicePreview> =>
    getAdminV1StoresStoreIdDraftOrdersIdInvoicePreview(
      { storeId, id },
      message ? { message } : undefined,
    ),
  /** Active manual methods for the complete dialog; readable with orders.write alone. */
  paymentOptions: async (storeId: string): Promise<DraftPaymentOption[]> =>
    (await getAdminV1StoresStoreIdDraftOrdersPaymentOptions({ storeId })).items,
  sendInvoice: (
    storeId: string,
    id: string,
    body: PostAdminV1StoresStoreIdDraftOrdersIdSendInvoiceBody = {},
  ): Promise<DraftOrder> => postAdminV1StoresStoreIdDraftOrdersIdSendInvoice({ storeId, id }, body),
  complete: (
    storeId: string,
    id: string,
    body: CompleteDraftOrderRequest,
  ): Promise<PostAdminV1StoresStoreIdDraftOrdersIdComplete201> =>
    postAdminV1StoresStoreIdDraftOrdersIdComplete({ storeId, id }, body),
  cancel: (storeId: string, id: string): Promise<DraftOrder> =>
    postAdminV1StoresStoreIdDraftOrdersIdCancel({ storeId, id }),
};
